import { Component, OnInit, inject, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Router } from '@angular/router'; // Import Router
import { Auth, IdTokenResult } from '@angular/fire/auth';
import { Firestore, collection, CollectionReference, DocumentData, addDoc, serverTimestamp, doc, deleteDoc, getDocs, QuerySnapshot, updateDoc, getDoc, setDoc, writeBatch } from '@angular/fire/firestore';
import { Storage, ref, listAll, deleteObject } from '@angular/fire/storage';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { ProjectCreateDialogComponent } from '../project-create-dialog/project-create-dialog.component';

interface Project extends DocumentData {
  id: string;
  name: string;
  description?: string;
  creationDate?: any;
}

@Component({
  selector: 'app-projects-list',
  templateUrl: './projects-list.component.html',
  styleUrls: ['./projects-list.component.scss'],
  providers: [DialogService, ConfirmationService, MessageService]
})
export class ProjectsListComponent implements OnInit, OnDestroy {

  private auth: Auth = inject(Auth);
  private firestore: Firestore = inject(Firestore);
  private storage: Storage = inject(Storage);
  private dialogService: DialogService = inject(DialogService);
  private confirmationService: ConfirmationService = inject(ConfirmationService);
  private messageService: MessageService = inject(MessageService);
  private router: Router = inject(Router); // Inject Router
  private cd: ChangeDetectorRef = inject(ChangeDetectorRef); // Inject ChangeDetectorRef
  private destroy$ = new Subject<void>();

  projects$: Observable<Project[] | null> = of(null); // Observable for async pipe if needed elsewhere, but filtering uses local array
  private allProjects: Project[] = []; // Holds the full list of projects
  filteredProjects: Project[] = []; // Holds the currently displayed projects
  isLoading = true;
  error: string | null = null;
  clientId: string | null = null;
  private projectsCollectionRef: CollectionReference<DocumentData> | null = null;

  dialogRef: DynamicDialogRef | undefined;
  
  // Re-added searchTerm property
  searchTerm: string = '';
  // viewMode remains removed
  
  // Property for keyboard navigation indicator
  keyboardFocusVisible: boolean = false;
  
  // Property to track which menu is open
  openMenuIndex: number | null = null;

  ngOnInit(): void {
    // Directly attach the listener
    const unsubscribe = this.auth.onIdTokenChanged(user => {
        if (user) {
          user.getIdTokenResult().then(
            idTokenResult => this.processTokenResult(idTokenResult),
            error => this.handleAuthError(error)
          );
        } else {
          this.handleNoUser();
        }
      }, error => {
          this.handleAuthError(error);
      });

      // Ensure we unsubscribe when the component is destroyed
      this.destroy$.subscribe(() => unsubscribe());
      
      // Setup keyboard navigation detection
      this.setupKeyboardNavigation();
  }
  
  // Setup keyboard navigation detection
  private setupKeyboardNavigation(): void {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        this.keyboardFocusVisible = true;
      }
    });
    
    document.addEventListener('mousedown', () => {
      this.keyboardFocusVisible = false;
    });
  }

  // Process token and fetch data using getDocs
  private async processTokenResult(tokenResult: IdTokenResult | null): Promise<void> {
      this.isLoading = true;

      if (!tokenResult) {
          this.handleNoUser();
          return;
      }

      // Set clientId and collectionRef
      if (tokenResult.claims['clientId'] && !this.clientId) {
          this.clientId = tokenResult.claims['clientId'] as string;
          this.projectsCollectionRef = collection(this.firestore, `clients/${this.clientId}/projects`);
      } else if (!tokenResult.claims['clientId']) {
          this.showError('Could not verify company information.');
          this.isLoading = false;
          this.projects$ = of([]);
          return;
      }

      // Fetch data using getDocs
      if (this.clientId && this.projectsCollectionRef) {
          try {
              // Check if the client document exists first
              const clientDocRef = doc(this.firestore, `clients/${this.clientId}`);
              const clientDoc = await getDoc(clientDocRef);
              
              // If client document doesn't exist, create it
              if (!clientDoc.exists()) {
                  console.log(`Creating client document for ${this.clientId}`);
                  await setDoc(clientDocRef, {
                      createdAt: new Date(),
                      id: this.clientId
                  });
              }
              
              // Now fetch projects
              const querySnapshot = await getDocs(this.projectsCollectionRef);
              const projects = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              } as Project));
              
              this.allProjects = [...projects]; // Store the full list
              this.projects$ = of(this.allProjects); // Update observable if needed
              this.filterProjects(); // Call filterProjects after fetching
              this.isLoading = false;
          } catch (err) {
              console.error('Error fetching projects with getDocs:', err);
              this.showError('Failed to load projects.');
              this.isLoading = false;
              this.allProjects = [];
              this.projects$ = of([]);
              this.filteredProjects = []; // Clear filtered list on error
          }
      } else {
          console.error('Company ID or collection ref missing before getDocs.');
          this.showError('Could not prepare data request.');
          this.isLoading = false;
          this.projects$ = of([]);
          this.filteredProjects = [];
      }
  }

  // Handle errors during auth state check
  private handleAuthError(error: any): void {
      console.error('Authentication error:', error);
      this.showError('Authentication error.');
      this.isLoading = false;
      this.allProjects = [];
      this.projects$ = of([]);
      this.filteredProjects = [];
  }

  // Handle the case where there is no user
  private handleNoUser(): void {
      this.showError('Please log in to view projects.');
      this.isLoading = false;
      this.allProjects = [];
      this.projects$ = of([]);
      this.filteredProjects = [];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up event listeners
    document.removeEventListener('keydown', () => {});
    document.removeEventListener('mousedown', () => {});
  }

  // Re-added filterProjects method
  // Filter projects based on search term using the local allProjects array
  filterProjects(): void {
    if (!this.allProjects) {
      this.filteredProjects = [];
      return;
    }

    const searchTermLower = this.searchTerm.toLowerCase().trim();

    if (!searchTermLower) {
      this.filteredProjects = [...this.allProjects]; // Show all if search is empty
    } else {
      this.filteredProjects = this.allProjects.filter(project =>
        project.name.toLowerCase().includes(searchTermLower) ||
        (project.description && project.description.toLowerCase().includes(searchTermLower))
      );
    }
  }

  // setViewMode remains removed

  openCreateProjectDialog(): void {
    if (!this.clientId) {
      this.showError('Cannot create project: Company information missing.');
      return;
    }

    this.dialogRef = this.dialogService.open(ProjectCreateDialogComponent, {
      header: 'Create New Project',
      width: '50%',
      contentStyle: { "max-height": "500px", "overflow": "auto" },
      baseZIndex: 10000
    });

    this.dialogRef.onClose.pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        this.createProject(result.name, result.description);
      }
    });
  }

  async createProject(name: string, description: string): Promise<void> {
    if (!this.projectsCollectionRef) {
      this.showError('Cannot create project: Data connection error.');
      return;
    }
    try {
      await addDoc(this.projectsCollectionRef, {
        name: name,
        description: description,
        creationDate: serverTimestamp()
      });
      this.showSuccess('Project created successfully!');
      
      // Refresh projects list
      if (this.clientId && this.projectsCollectionRef) {
        const querySnapshot = await getDocs(this.projectsCollectionRef);
        const projects = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Project));
        
        this.allProjects = [...projects]; // Update the full list
        this.projects$ = of(this.allProjects); // Update observable if needed
        this.filterProjects(); // Re-apply filters after create
      }
    } catch (error) {
      console.error('Error creating project:', error);
      this.showError('Failed to create project.');
    }
  }

  confirmDeleteProject(project: Project): void {
    this.openMenuIndex = null; // Close menu
     
     if (!this.clientId) {
      this.showError('Cannot delete project: Company information missing.');
      return;
    }
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the project "${project.name}"? This action cannot be undone.`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteProject(project.id);
      },
      reject: () => {
        this.messageService.add({severity:'info', summary:'Cancelled', detail:'Project deletion cancelled.', life: 3000});
      }
    });
  }

  async deleteProject(projectId: string): Promise<void> {
    if (!this.clientId) {
      this.showError('Cannot delete project: Data connection error.');
      return;
    }

    const projectDocRef = doc(this.firestore, `clients/${this.clientId}/projects/${projectId}`);

    try {
      // 1. Delete Storage Contents (already implemented)
      const storageDeleted = await this.deleteProjectStorage(this.clientId, projectId);
      if (!storageDeleted) {
        this.showError('Failed to delete project storage. Aborting Firestore deletion.');
        return; // Stop if storage deletion fails
      }

      // 2. Delete Firestore Subcollections (e.g., 'experiences')
      // Add other known subcollections here if necessary
      const experiencesCollectionRef = collection(projectDocRef, 'experiences');
      await this.deleteCollection(experiencesCollectionRef);
      
      // Add calls to delete other subcollections here:
      // const anotherSubCollectionRef = collection(projectDocRef, 'anotherSubCollection');
      // await this.deleteCollection(anotherSubCollectionRef);


      // 3. Delete the main project document
      await deleteDoc(projectDocRef);

      this.showSuccess('Project and its data deleted successfully!');

      // 4. Refresh projects list (already implemented)
      if (this.clientId && this.projectsCollectionRef) {
        const querySnapshot = await getDocs(this.projectsCollectionRef);
        const projects = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Project));

        this.allProjects = [...projects]; // Update the full list
        this.projects$ = of(this.allProjects); // Update observable if needed
        this.filterProjects(); // Re-apply filters after delete
      }

    } catch (error) {
      console.error('Error deleting project or its subcollections:', error);
      this.showError('Failed to delete project data.');
      // Consider adding more specific error handling or rollback logic if needed
    }
  }

  /**
   * Deletes all documents within a given collection using batched writes.
   * Does not handle nested subcollections within the documents being deleted.
   * @param collectionRef Reference to the collection to delete.
   */
  private async deleteCollection(collectionRef: CollectionReference): Promise<void> {
    try {
      const querySnapshot = await getDocs(collectionRef);
      if (querySnapshot.empty) {
        console.log(`Collection ${collectionRef.path} is empty or does not exist. Skipping deletion.`);
        return; // Nothing to delete
      }

      const batch = writeBatch(this.firestore);
      querySnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      console.log(`Successfully deleted documents in collection: ${collectionRef.path}`);
    } catch (error) {
      console.error(`Error deleting collection ${collectionRef.path}:`, error);
      // Re-throw the error to be caught by the calling function (deleteProject)
      throw new Error(`Failed to delete subcollection ${collectionRef.path}.`);
    }
  }

  /**
   * Deletes all files in the project's storage directory
   * @param clientId The client ID
   * @param projectId The project ID
   * @returns Promise<boolean> True if deletion was successful or directory didn't exist, false otherwise
   */
  private async deleteProjectStorage(clientId: string, projectId: string): Promise<boolean> {
    try {
      // Create a reference to the project directory in storage
      const storageRef = ref(this.storage, `clients/${clientId}/projects/${projectId}`);
      
      try {
        // List all items in the directory
        const listResult = await listAll(storageRef);
        
        // If there are no items, consider it a success (directory might not exist)
        if (listResult.items.length === 0 && listResult.prefixes.length === 0) {
          console.log('No files found in storage for this project.');
          return true;
        }
        
        // Delete all files in the directory
        const deletePromises = listResult.items.map(itemRef => {
          return deleteObject(itemRef);
        });
        
        // Handle potential subdirectories recursively
        const subDirPromises = listResult.prefixes.map(prefix => {
          return this.deleteStorageDirectory(prefix);
        });
        
        // Wait for all deletions to complete
        await Promise.all([...deletePromises, ...subDirPromises]);
        console.log('Project storage deleted successfully');
        return true;
      } catch (error: any) {
        // If error is because the directory doesn't exist, that's fine
        if (error.code === 'storage/object-not-found') {
          console.log('Storage directory does not exist, proceeding with Firestore deletion');
          return true;
        }
        throw error;
      }
    } catch (error) {
      console.error('Error deleting project storage:', error);
      return false;
    }
  }
  
  /**
   * Recursively deletes a storage directory and all its contents
   * @param directoryRef Reference to the storage directory
   * @returns Promise<void>
   */
  private async deleteStorageDirectory(directoryRef: any): Promise<void> {
    try {
      // List all items in the directory
      const listResult = await listAll(directoryRef);
      
      // Delete all files in the directory
      const deletePromises = listResult.items.map(itemRef => {
        return deleteObject(itemRef);
      });
      
      // Handle potential subdirectories recursively
      const subDirPromises = listResult.prefixes.map(prefix => {
        return this.deleteStorageDirectory(prefix);
      });
      
      // Wait for all deletions to complete
      await Promise.all([...deletePromises, ...subDirPromises]);
    } catch (error) {
      console.error('Error deleting storage directory:', error);
      throw error;
    }
  }

  // Method to navigate to project experiences
  navigateToProjectExperiences(projectId: string): void {
    if (!this.clientId) {
      this.showError('Cannot navigate: Company information missing.');
      console.error('Company ID is missing, cannot navigate.');
      return;
    }
    this.router.navigate(['/', this.clientId, 'projects', projectId, 'experiences']);
  }

  // Method to navigate to project analytics
  navigateToProjectAnalytics(projectId: string, projectName: string): void {
    if (!this.clientId) {
      this.showError('Cannot navigate: Company information missing.');
      console.error('Company ID is missing, cannot navigate.');
      return;
    }
    this.router.navigate(['/', this.clientId, 'projects', projectId, 'analytics'], {
      queryParams: { name: projectName }
    });
  }



  // Method to toggle the project menu using the index

  // Method to toggle custom menu
  toggleCustomMenu(event: Event, index: number): void {
    event.stopPropagation();
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  // Close menu when clicking outside
  @HostListener('document:click', ['$event'])
  closeMenu(event: Event): void {
    this.openMenuIndex = null;
  }

  // Method to handle menu item actions (keeping for potential future use)
  handleMenuAction(action: string, project: Project): void {
    this.openMenuIndex = null; // Close menu after action
    switch (action) {
      case 'edit':
        this.editProject(project);
        break;
      case 'delete':
        this.confirmDeleteProject(project);
        break;
      default:
        console.warn('Unknown menu action:', action);
    }
  }

  // Method to handle edit project button click
  editProject(project: Project): void {
    this.openMenuIndex = null; // Close menu
    
    if (!this.clientId) {
      this.showError('Cannot edit project: Company information missing.');
      return;
    }

    this.dialogRef = this.dialogService.open(ProjectCreateDialogComponent, {
      header: 'Edit Project',
      width: '50%',
      contentStyle: { "max-height": "500px", "overflow": "auto" },
      baseZIndex: 10000,
      data: {
        name: project.name,
        description: project.description || ''
      }
    });

    this.dialogRef.onClose.pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        this.updateProject(project.id, result.name, result.description);
      }
    });
  }

  // Method to update project in Firestore
  async updateProject(projectId: string, name: string, description: string): Promise<void> {
    if (!this.clientId) {
      this.showError('Cannot update project: Data connection error.');
      return;
    }
    
    try {
      const projectDocRef = doc(this.firestore, `clients/${this.clientId}/projects/${projectId}`);
      await updateDoc(projectDocRef, {
        name: name,
        description: description,
        updatedDate: serverTimestamp()
      });
      
      this.showSuccess('Project updated successfully!');
      
      // Refresh projects list
      if (this.clientId && this.projectsCollectionRef) {
        const querySnapshot = await getDocs(this.projectsCollectionRef);
        const projects = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Project));
        
        this.allProjects = [...projects]; // Update the full list
        this.projects$ = of(this.allProjects); // Update observable if needed
        this.filterProjects(); // Re-apply filters after update
      }
    } catch (error) {
      console.error('Error updating project:', error);
      this.showError('Failed to update project.');
    }
  }


  onMenuShow(projectName: string): void {
    console.log(`Menu for ${projectName} is showing.`);
  }

  onMenuHide(projectName: string): void {
    console.log(`Menu for ${projectName} is hiding.`);
  }

  private showSuccess(detail: string): void { // Renamed parameter
    this.messageService.add({ severity: 'success', summary: 'Success', detail: detail, life: 3000 });
  }

  private showError(detail: string): void { // Renamed parameter
    this.messageService.add({ severity: 'error', summary: 'Error', detail: detail, life: 5000 });
    this.error = detail; // Use renamed parameter
  }
}
